import React from 'react';
import { MaterializedView } from '@/types';
import { AccessTime, LocationOn, People, Warning } from '@mui/icons-material';

interface CalendarViewProps {
    materializedView: MaterializedView;
}

const HOURS = Array.from({ length: 9 }, (_, i) => i + 9); // 09:00 to 17:00

const formatTime = (dateStr: string): string => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const getEventColor = (_eventId: string, title: string): { bg: string; border: string; text: string } => {
    if (title.toLowerCase().includes('all-hands'))
        return { bg: 'bg-red-500/20', border: 'border-red-500/50', text: 'text-red-400' };
    if (title.toLowerCase().includes('presentation'))
        return { bg: 'bg-amber-500/20', border: 'border-amber-500/50', text: 'text-amber-400' };
    return { bg: 'bg-blue-500/20', border: 'border-blue-500/50', text: 'text-blue-400' };
};

const CalendarView: React.FC<CalendarViewProps> = ({ materializedView }) => {
    const events = materializedView.calendar || [];
    const simTimeSeconds = materializedView.simulationTimeSeconds ?? 0;

    // Simulation starts at 09:00, so current sim time = 9:00 + simTimeSeconds
    const simHour = 9 + Math.floor(simTimeSeconds / 3600);
    const simMinute = Math.floor((simTimeSeconds % 3600) / 60);
    const currentTimeMinutes = simHour * 60 + simMinute; // total minutes from midnight

    return (
        <div className="flex flex-col h-full bg-slate-800 text-white">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-600">
                <div className="flex items-center gap-3">
                    <AccessTime className="text-blue-400" />
                    <div>
                        <h2 className="text-lg font-bold text-white">Calendar</h2>
                        <p className="text-xs text-slate-300">Today's Schedule</p>
                    </div>
                    <div className="ml-auto px-3 py-1 bg-blue-500/20 rounded-full">
                        <span className="text-xs font-bold text-blue-400 font-mono">
                            {String(simHour).padStart(2, '0')}:{String(simMinute).padStart(2, '0')}
                        </span>
                    </div>
                </div>
            </div>

            {/* Day view */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="relative">
                    {HOURS.map((hour) => {
                        const hourMinutes = hour * 60;
                        const eventsAtHour = events.filter((e) => {
                            const startH = new Date(e.startTime).getUTCHours();
                            return startH === hour;
                        });

                        return (
                            <div key={hour} className="flex min-h-[80px] relative">
                                {/* Time label */}
                                <div className="w-16 shrink-0 text-xs text-slate-400 font-mono pt-1">
                                    {String(hour).padStart(2, '0')}:00
                                </div>

                                {/* Content area */}
                                <div className="flex-1 border-t border-slate-600/50 pl-4 py-1 relative">
                                    {/* Current time indicator */}
                                    {currentTimeMinutes >= hourMinutes && currentTimeMinutes < hourMinutes + 60 && (
                                        <div
                                            className="absolute left-0 right-0 z-10 flex items-center"
                                            style={{ top: `${((currentTimeMinutes - hourMinutes) / 60) * 80}px` }}
                                        >
                                            <div className="size-2 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)]"></div>
                                            <div className="flex-1 h-px bg-blue-500/60"></div>
                                        </div>
                                    )}

                                    {/* Events */}
                                    {eventsAtHour.map((event) => {
                                        const colors = getEventColor(event.id, event.title);
                                        const isAllHands = event.title.toLowerCase().includes('all-hands');
                                        const isPresentation = event.title.toLowerCase().includes('presentation');

                                        return (
                                            <div
                                                key={event.id}
                                                className={`${colors.bg} border-l-2 ${colors.border} rounded-r-lg p-3 mb-2`}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <h4 className={`text-sm font-bold ${colors.text}`}>
                                                                {event.title}
                                                            </h4>
                                                            {isAllHands && (
                                                                <span className="text-[10px] bg-red-500/30 text-red-400 px-1.5 py-0.5 rounded font-bold uppercase">
                                                                    Mandatory
                                                                </span>
                                                            )}
                                                            {isPresentation && (
                                                                <Warning fontSize="small" className="text-amber-400" style={{ fontSize: '14px' }} />
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-3 mt-1">
                                                            <span className="text-[11px] text-slate-300 font-mono">
                                                                {formatTime(event.startTime)} – {formatTime(event.endTime)}
                                                            </span>
                                                        </div>
                                                        {event.location && (
                                                            <div className="flex items-center gap-1 mt-1">
                                                                <LocationOn style={{ fontSize: '12px' }} className="text-slate-400" />
                                                                <span className="text-[11px] text-slate-400">{event.location}</span>
                                                            </div>
                                                        )}
                                                        {event.description && (
                                                            <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                                                                {event.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                {event.attendees.length > 0 && (
                                                    <div className="flex items-center gap-1 mt-2">
                                                        <People style={{ fontSize: '12px' }} className="text-slate-400" />
                                                        <span className="text-[10px] text-slate-400">
                                                            {event.attendees.join(', ')}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default CalendarView;
